import { FC, useCallback, useContext } from 'react';
import { Avatar, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Language } from '@mui/icons-material';

import { trackEvent } from '@cvt/tracking';

import { LayoutContext } from '@shared/contexts/LayoutContext';

import { useUniversityCrud } from '../hooks/useUniversityCrud';

interface Props {
  university: Universities.University;
  specialization?: Specializations.Specialization;
}

export const UniversityAdmissionsButton: FC<Props> = ({ university, specialization }) => {

  const { isDesktop } = useContext(LayoutContext);

  const { applyToUniversity } = useUniversityCrud();

  const onApply = useCallback(async () => {
    try {
      applyToUniversity.mutateAsync({
        university: university.id,
        specialization: specialization?.id,
      });
    } catch(err) {
      console.error(err);
    }

    trackEvent('apply', {
      university: university.id,
      specialization: specialization?.id,
    });

    if (university.admissionsPageLink) {
      window.open(university.admissionsPageLink, '_blank');
    }
  }, [applyToUniversity, university.id, specialization, university.admissionsPageLink]);

  if (!university.admissionsPageLink) {
    return null;
  }

  return (
    <Box
      position={university.sponsored ? 'sticky' : undefined}
      bottom={isDesktop ? 'env(safe-area-inset-bottom)' : 'calc(env(safe-area-inset-bottom) + 55px)'}
      py={2}
      px={2}
      sx={theme => ({
        background: `linear-gradient(180deg,transparent 0%, ${theme.palette.background.default} 80%)`,
      })}
    >
      <Paper
        component={Box}
        p={1}
        width="100%"
        variant="outlined"
        sx={theme => ({
          borderColor: theme.palette.common.black,
          backgroundColor: theme.palette.tertiary.light,
        })}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} minWidth={0}>
            <Avatar variant="rounded" sx={theme => ({ width: 32, height0: 32, backgroundColor: 'transparent', color: 'common.black' })}>
              <Language/>
            </Avatar>
            <Box overflow="hidden" minWidth={0}>
              <Typography variant="subtitle1" lineHeight={1} mb={0.4}>Admissions</Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: 'block',
                }}
              >
                {university.website}
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" color="primary" size="small" onClick={onApply}>Apply</Button>
        </Stack>
      </Paper>
    </Box>
  );
};